import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import _, { isEmpty } from 'lodash';
import { toastConfig } from 'config';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/material/styles';
import excel from 'assets/images/icons/excel.svg';
import ErrorModal from 'pages/comptroller/ErrorModal';
import { useLocation, useNavigate } from 'react-router-dom';
import { memo, useCallback, useEffect, useState } from 'react';
import { CheckSquareOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Form, Popover, Row, Upload, Spin } from 'antd';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { useGetAllIdentityCaseMutation } from 'store/features/process/processApi';
import { exportExcelFile, handleFileUpload } from 'pages/globals/FileUploadExcel';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { formatNumber, utcConverter } from '../../lease-record-management-v9-new-version/utils/helperFunc';

const { Dragger } = Upload;

const INITIAL_EXPORT_DATA = [
	{
		'Reference Id': '',
		'Lease Title': '',
		'Lease Type': '',
		'Lessor Code': '',
		'Payment Frequency': '',
		'Lease Start Date (YYYY-MM-DD)': '',
		'Rent (From Month)': '',
		'Rent (To Month)': '',
		'Rent Amount': '',
		'Advance (From Month)': '',
		'Advance (To Month)': '',
		'Advance Adjustment Amount': '',
		'Incremental Borrowing Rate': '',
		'Escalation (%)': '',
		'Payment Modality': '',
		'Upfront Lease Payment': '',
		'Initial Direct Cost': '',
		'Security Deposit': '',
		'Cash Incentive': '',
	},
];

const TABLE_COLUMNS = [
	{ id: 'referenceId', align: 'center', disablePadding: true, label: 'Reference Id', key: 'Reference Id' },
	{ id: 'leaseTitle', align: 'center', disablePadding: true, label: 'Lease Title', key: 'Lease Title' },
	{ id: 'leaseType', align: 'center', disablePadding: true, label: 'Lease Type', key: 'Lease Type' },
	{ id: 'lessorName', align: 'center', disablePadding: true, label: 'Lessor Code', key: 'Lessor Code' },
	{
		id: 'paymentFrequency',
		align: 'center',
		disablePadding: true,
		label: 'Payment Frequency',
		key: 'Payment Frequency',
	},
	{
		id: 'leaseStartDate',
		align: 'center',
		disablePadding: true,
		label: 'Lease Start Date (YYYY-MM-DD)',
		key: 'Lease Start Date (YYYY-MM-DD)',
	},
	{
		id: 'rentFromMonth',
		align: 'center',
		disablePadding: true,
		label: 'Rent (From Month)',
		key: 'Rent (From Month)',
	},
	{ id: 'rentToMonth', align: 'center', disablePadding: true, label: 'Rent (To Month)', key: 'Rent (To Month)' },
	{ id: 'rentAmount', align: 'center', disablePadding: true, label: 'Rent Amount', key: 'Rent Amount' },
	{
		id: 'advanceFromMonth',
		align: 'center',
		disablePadding: true,
		label: 'Advance (From Month)',
		key: 'Advance (From Month)',
	},
	{
		id: 'AdvanceToMonth',
		align: 'center',
		disablePadding: true,
		label: 'Advance (To Month)',
		key: 'Advance (To Month)',
	},
	{
		id: 'advanceAdjustmentAmount',
		align: 'center',
		disablePadding: true,
		label: 'Advance Adjustment Amount',
		key: 'Advance Adjustment Amount',
	},
	{
		id: 'internalBorrowingRate',
		align: 'center',
		disablePadding: true,
		label: 'Incremental Borrowing Rate',
		key: 'Incremental Borrowing Rate',
	},
	{ id: 'escalation', align: 'center', disablePadding: true, label: 'Escalation (%)', key: 'Escalation (%)' },
	{
		id: 'paymentModality',
		align: 'center',
		disablePadding: true,
		label: 'Payment Modality',
		key: 'Payment Modality',
	},
	{
		id: 'upfrontLeasePayment',
		align: 'center',
		disablePadding: true,
		label: 'Upfront Lease Payment',
		key: 'Upfront Lease Payment',
	},
	{
		id: 'initialDirectCost',
		align: 'center',
		disablePadding: true,
		label: 'Initial Direct Cost',
		key: 'Initial Direct Cost',
	},
	{
		id: 'securityDeposit',
		align: 'center',
		disablePadding: true,
		label: 'Security Deposit',
		key: 'Security Deposit',
	},
	{ id: 'cashIncentive', align: 'center', disablePadding: true, label: 'Cash Incentive', key: 'Cash Incentive' },
	{ id: 'lrmAction', align: 'center', disablePadding: true, label: 'Action', key: 'Action' },
];

const GUIDELINES_DATA = [
	['LEASE BULK UPLOAD GUIDELINES'],
	[''],
	['FIELD VALIDATIONS:'],
	[''],
	['1. Reference Id', 'Required - Unique identifier for each lease'],
	['2. Lease Title', 'Required - Name/title of the lease'],
	['3. Lease Type', 'Required - Type of lease'],
	['4. Lessor Code', 'Required - Name of the lessor'],
	['5. Payment Frequency', 'Required - Must be "Monthly" or "Quarterly"'],
	['6. Lease Start Date', 'Required - Format: YYYY-MM-DD'],
	['7. Rent (From Month)', 'Required - Numeric value or "-" if not applicable'],
	['8. Rent (To Month)', 'Required - Numeric value or "-" if not applicable'],
	['9. Rent Amount', 'Required - Numeric value or "-" if not applicable'],
	['10. Advance (From Month)', 'Required - Numeric value or "-" if not applicable'],
	['11. Advance (To Month)', 'Required - Numeric value or "-" if not applicable'],
	['12. Advance Adjustment Amount', 'Required - Numeric value or "-" if not applicable'],
	['13. Incremental Borrowing Rate', 'Required - Numeric value (percentage)'],
	['14. Escalation (%)', 'Required - Numeric value (percentage)'],
	['15. Payment Modality', 'Required - "Advance Basis" or "Accrual Basis"'],
	['16. Upfront Lease Payment', 'Optional - Numeric value (default: 0)'],
	['17. Initial Direct Cost', 'Optional - Numeric value (default: 0)'],
	['18. Security Deposit', 'Optional - Numeric value (default: 0)'],
	['19. Cash Incentive', 'Optional - Numeric value (default: 0)'],
	[''],
	['BUSINESS RULES:'],
	[''],
	['1. Sequential Periods:', 'Rent and Advance periods must be sequential within each Reference ID'],
	['2. From <= To Months:', 'From Month must be less than or equal to To Month in the same row'],
	['3. Consistency:', 'Fields like Lease Title, Lessor Code, Payment Frequency must be consistent within the same Reference ID'],
	['4. Rent vs Advance:', 'Max Rent (To Month) must be >= Max Advance (To Month)'],
	['5. Total Amounts:', 'Total Rent Amount must be >= Total Advance Adjustment Amount'],
	[''],
	['DATA ENTRY TIPS:'],
	[''],
	['• Use "-" for fields that are not applicable'],
	['• Ensure dates are in YYYY-MM-DD format'],
	['• All numeric fields should contain only numbers'],
	['• Payment Frequency: Use exact values "Monthly" or "Quarterly"'],
	['• Payment Modality: Use exact values "Advance Basis" or "Accrual Basis"'],
	['• Group rows by Reference Id for multiple rent/advance periods'],
	[''],
	['EXAMPLE SCENARIOS:'],
	[''],
	['Scenario 1: Simple Lease'],
	['- Single Reference ID with one rent period and one advance period'],
	['- All required fields must be filled'],
	[''],
	['Scenario 2: Complex Lease with Multiple Periods'],
	['- Same Reference ID with multiple rows for different rent/advance periods'],
	['- Periods must be sequential (e.g., Month 1-12, then 13-24, etc.)'],
	['- Consistent information across all rows for the same Reference ID'],
	[''],
	['ERROR RESOLUTION:'],
	[''],
	['• Red borders indicate validation errors'],
	['• Click "Show Errors" button to see detailed error messages'],
	['• Fix all errors before submitting'],
	['• Download this template again if needed'],
	[''],
	['IMPORTANT NOTES:'],
	[''],
	['• This bulk upload is for LEASE CREATION only'],
	['• Each Reference ID represents a new lease to be created'],
	['• Ensure Reference IDs are unique across the entire upload'],
	['• All rows with the same Reference ID will be treated as one lease'],
];

const parseAndFormatExcelDate = (excelDate) => {
	if (excelDate instanceof Date) {
		if (!isNaN(excelDate.getTime())) {
			const year = excelDate.getUTCFullYear();
			const month = String(excelDate.getUTCMonth() + 1).padStart(2, '0');
			const day = String(excelDate.getUTCDate()).padStart(2, '0');
			const result = `${year}-${month}-${day}`;

			return result;
		}

		return '';
	}

	if (typeof excelDate === 'number') {
		const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
		const EXCEL_START_DATE = new Date(1900, 0, 1);

		const daysToAdd = excelDate > 59 ? excelDate - 1 : excelDate;

		const date = new Date(EXCEL_START_DATE.getTime() + daysToAdd * MILLISECONDS_PER_DAY);

		const year = date.getUTCFullYear();
		const month = String(date.getUTCMonth() + 1).padStart(2, '0');
		const day = String(date.getUTCDate()).padStart(2, '0');
		const result = `${year}-${month}-${day}`;

		return result;
	}

	if (typeof excelDate === 'string') {
		const trimmed = excelDate.trim();

		if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
			return trimmed;
		}

		const ddmmyyyySlash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
		const ddmmyyyyMatch = trimmed.match(ddmmyyyySlash);

		if (ddmmyyyyMatch) {
			const day = ddmmyyyyMatch[1].padStart(2, '0');
			const month = ddmmyyyyMatch[2].padStart(2, '0');
			const year = ddmmyyyyMatch[3];
			const result = `${year}-${month}-${day}`;

			return result;
		}

		const mmddyyyySlash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
		const mmddyyyyMatch = trimmed.match(mmddyyyySlash);

		if (mmddyyyyMatch && parseInt(mmddyyyyMatch[1]) <= 12) {
			const month = mmddyyyyMatch[1].padStart(2, '0');
			const day = mmddyyyyMatch[2].padStart(2, '0');
			const year = mmddyyyyMatch[3];
			const result = `${year}-${month}-${day}`;

			return result;
		}

		const ddmmyyyyDash = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
		const ddmmyyyyDashMatch = trimmed.match(ddmmyyyyDash);

		if (ddmmyyyyDashMatch) {
			const day = ddmmyyyyDashMatch[1].padStart(2, '0');
			const month = ddmmyyyyDashMatch[2].padStart(2, '0');
			const year = ddmmyyyyDashMatch[3];
			const result = `${year}-${month}-${day}`;

			return result;
		}

		const yyyymmddSlash = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/;
		const yyyymmddMatch = trimmed.match(yyyymmddSlash);

		if (yyyymmddMatch) {
			const year = yyyymmddMatch[1];
			const month = yyyymmddMatch[2].padStart(2, '0');
			const day = yyyymmddMatch[3].padStart(2, '0');
			const result = `${year}-${month}-${day}`;

			return result;
		}

		if (trimmed.includes('T') || trimmed.includes(' ')) {
			const dateOnly = trimmed.split('T')[0].split(' ')[0];

			if (dateOnly.match(/^\d{4}-\d{2}-\d{2}$/)) {
				return dateOnly;
			}
		}

		// no-useless-escape
		const parts = trimmed.split(/[./-]/);
		if (parts.length === 3) {
			const nums = parts.map((p) => parseInt(p, 10));

			let year, month, day;

			if (nums[0] > 31) {
				year = nums[0];
				month = nums[1];
				day = nums[2];
			} else if (nums[2] > 31) {
				if (nums[0] <= 12 && nums[1] <= 12) {
					day = nums[0];
					month = nums[1];
				} else if (nums[0] <= 12) {
					month = nums[0];
					day = nums[1];
				} else {
					day = nums[0];
					month = nums[1];
				}
				year = nums[2];
			}

			if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
				const result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

				return result;
			}
		}
	}

	return '';
};

const exportExcelFileWithGuidelines = (templateData, fileName) => {
	const wb = XLSX.utils.book_new();

	const templateWs = XLSX.utils.json_to_sheet(templateData);
	const columnWidths = Array(Object.keys(templateData[0]).length).fill({ wch: 20 });
	templateWs['!cols'] = columnWidths;
	XLSX.utils.book_append_sheet(wb, templateWs, 'Template');

	const guidelinesWs = XLSX.utils.aoa_to_sheet(GUIDELINES_DATA);

	guidelinesWs['!cols'] = [{ wch: 30 }, { wch: 80 }];

	const range = XLSX.utils.decode_range(guidelinesWs['!ref']);

	for (let R = range.s.r; R <= range.e.r; ++R) {
		for (let C = range.s.c; C <= range.e.c; ++C) {
			const cellAddress = { c: C, r: R };
			const cellRef = XLSX.utils.encode_cell(cellAddress);

			if (!guidelinesWs[cellRef]) continue;

			guidelinesWs[cellRef].s = guidelinesWs[cellRef].s || {};

			if (R === 0) {
				guidelinesWs[cellRef].s.font = { bold: true, sz: 16 };
				guidelinesWs[cellRef].s.alignment = { horizontal: 'center' };
			} else if (
				GUIDELINES_DATA[R] &&
				GUIDELINES_DATA[R][0] &&
				(GUIDELINES_DATA[R][0].includes('VALIDATIONS:') ||
					GUIDELINES_DATA[R][0].includes('BUSINESS RULES:') ||
					GUIDELINES_DATA[R][0].includes('DATA ENTRY TIPS:') ||
					GUIDELINES_DATA[R][0].includes('EXAMPLE SCENARIOS:') ||
					GUIDELINES_DATA[R][0].includes('ERROR RESOLUTION:') ||
					GUIDELINES_DATA[R][0].includes('IMPORTANT NOTES:'))
			) {
				guidelinesWs[cellRef].s.font = { bold: true, sz: 12 };
				guidelinesWs[cellRef].s.fill = { fgColor: { rgb: 'E6E6FA' } };
			} else if (GUIDELINES_DATA[R] && GUIDELINES_DATA[R][0] && /^\d+\./.test(GUIDELINES_DATA[R][0])) {
				guidelinesWs[cellRef].s.font = { bold: true };
			}
		}
	}

	XLSX.utils.book_append_sheet(wb, guidelinesWs, 'Guidelines');

	const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
	const blob = new Blob([wbout], { type: 'application/octet-stream' });

	const url = window.URL.createObjectURL(blob);
	const anchor = document.createElement('a');

	anchor.href = url;
	anchor.download = fileName;

	anchor.click();

	window.URL.revokeObjectURL(url);
};

const transformErrors = (errorObj, index) => {
	if (errorObj.valid !== undefined) {
		const { valid, ...rest } = errorObj;

		errorObj = rest;
	}

	return Object.entries(errorObj).map(([field, message]) => ({
		DataIndex: index,
		field: field.startsWith('Error_') ? 'Validation Error' : field,
		message: message,
	}));
};

const formatDate = (dateStr) => {
	if (typeof dateStr !== 'string') return '';

	let [year, month, day] = dateStr.split('-').map((num) => num.padStart(2, '0'));

	return `${year}-${month}-${day}`;
};

const getDateParts = (date) => {
	if (!date) return { day: '', month: '', year: '' };

	const [year, month, day] = date.split('-').map((num) => num.padStart(2, '0'));

	return { day, month, year };
};

const processLeaseData = (data) => {
	const formatAmount = (amount) => {
		return amount.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	};

	const maxLeaseTerm = data.reduce((max, item) => {
		const rentToMonth = item['Rent (To Month)'] === '-' ? 0 : Number(item['Rent (To Month)']);
		const advanceToMonth = item['Advance (To Month)'] === '-' ? 0 : Number(item['Advance (To Month)']);

		const correctLeaseTerm = Math.max(max, rentToMonth, advanceToMonth);

		return data['Payment Frequency']?.toLowerCase() === 'quarterly' ? correctLeaseTerm / 3 : correctLeaseTerm;
	}, 0);

	const leaseTerm = maxLeaseTerm || 0;

	const inputChangeAdvanceAmounts = new Array(leaseTerm).fill(0);

	const advanceAmountList = [];

	let totalAdvanceAmount = 0;
	let maxAdvanceToMonth = 0;

	data.forEach((item) => {
		if (item['Advance (From Month)'] !== '-' && item['Advance (To Month)'] !== '-' && item['Advance Adjustment Amount'] !== '-') {
			const from = Number(item['Advance (From Month)']);
			const to = Number(item['Advance (To Month)']);
			const amount = Number(item['Advance Adjustment Amount']);

			maxAdvanceToMonth = Math.max(maxAdvanceToMonth, to);

			totalAdvanceAmount += amount;

			for (let month = from; month <= to; month++) {
				if (month - 1 < leaseTerm) {
					inputChangeAdvanceAmounts[month - 1] = amount;
				}
			}

			advanceAmountList.push({
				from: from,
				to: to.toString(),
				amount: formatAmount(amount),
			});
		}
	});

	const inputChangeGrossRents = new Array(leaseTerm).fill(0);

	const grossRentList = [];

	let totalRentAmount = 0;

	data.forEach((item) => {
		if (item['Rent (From Month)'] !== '-' && item['Rent (To Month)'] !== '-' && item['Rent Amount'] !== '-') {
			const from = Number(item['Rent (From Month)']);
			const to = Number(item['Rent (To Month)']);
			const amount = Number(item['Rent Amount']);

			totalRentAmount += amount;

			for (let month = from; month <= to; month++) {
				if (month - 1 < leaseTerm) {
					inputChangeGrossRents[month - 1] = amount;
				}
			}

			grossRentList.push({
				from: from,
				to: to.toString(),
				amount: formatAmount(amount),
				remarks: '',
			});
		}
	});

	advanceAmountList.sort((a, b) => a.from - b.from);
	grossRentList.sort((a, b) => a.from - b.from);

	const uniqueAdvanceList = advanceAmountList.filter((item, index, self) => index === self.findIndex((t) => t.from === item.from));
	const uniqueRentList = grossRentList.filter((item, index, self) => index === self.findIndex((t) => t.from === item.from));

	return {
		inputChangeAdvanceAmounts,
		advanceAmountList: uniqueAdvanceList,
		totalAdvanceAmount,
		maxAdvanceToMonth,

		inputChangeGrossRents,
		grossRentList: uniqueRentList,
		totalRentAmount,

		maxLeaseTerm,
		leaseTerm,
	};
};

const validateConsistentFields = (items) => {
	if (items.length < 2) {
		return [];
	}

	const errors = [];

	const fieldsToCheck = [
		'Lease Title',
		'Lease Type',
		'Lessor Code',
		'Payment Frequency',
		'Lease Start Date (YYYY-MM-DD)',
		'Incremental Borrowing Rate',
		'Escalation (%)',
	];

	const referenceItem = items[0];

	for (let i = 1; i < items.length; i++) {
		const currentItem = items[i];

		fieldsToCheck.forEach((field) => {
			let referenceValue = referenceItem[field];
			let currentValue = currentItem[field];

			if (field === 'Lease Start Date (YYYY-MM-DD)') {
				referenceValue = parseAndFormatExcelDate(referenceValue);
				currentValue = parseAndFormatExcelDate(currentValue);
			}

			if (referenceValue !== currentValue) {
				errors.push({
					rowIndex: currentItem['Data No.'],
					referenceId: currentItem['Reference Id'],
					fromMonth: currentItem['Rent (From Month)'],
					toMonth: currentItem['Rent (To Month)'],
					message: `${field} must be consistent within the same reference ID group. Expected: ${referenceValue}`,
				});
			}
		});
	}

	return errors;
};

function OptimizedTableCellComponent({ column, lrm, getCellStyle, getCellContent }) {
	const align = column.key === 'Lease Title' || column.key === 'Lessor Code' ? 'left' : 'center';

	if (column.key === 'Action') {
		return <TableCell align={align}>{getCellContent(column.key, null, lrm)}</TableCell>;
	}

	return (
		<TableCell align={align}>
			<div style={getCellStyle(column.key, lrm[column.key], lrm)}>{getCellContent(column.key, lrm[column.key], lrm)}</div>
		</TableCell>
	);
}

const OptimizedTableCell = memo(OptimizedTableCellComponent);

const getRowStyle = (lrm, tableData, index) => {
	// Get reference ID
	const referenceId = lrm['Reference Id'];

	// Get next row
	const nextRow = index < tableData.length - 1 ? tableData[index + 1] : null;

	// Check if this row ends a reference ID group
	const isRefIdEnd = !nextRow || nextRow['Reference Id'] !== referenceId;

	// Check if this is the last row in the entire table
	const isLastRow = index === tableData.length - 1;

	// Initialize style object
	const style = {};

	// Only add bottom borders between reference ID groups
	if (isRefIdEnd && !isLastRow) {
		// This is the last row of a reference ID group, but not the last row in the table
		style.borderBottom = '2px solid #000000';
	}

	// Subtle background alternation for better readability
	if (lrm.groupId % 2 === 0) {
		style.backgroundColor = '#e0e0e0'; // Light gray background for even groups
	}

	return style;
};

function OptimizedTableRowComponent({ lrm, index, columns, getCellStyle, getCellContent, tableData, getRowStyle }) {
	const rowStyle = getRowStyle(lrm, tableData, index) ?? {};

	return (
		<TableRow
			hover
			role="checkbox"
			sx={{
				'&:last-child td, &:last-child th': { border: 0 },
				...rowStyle,
				...(rowStyle.borderBottom && {
					'& td': {
						borderBottom: `${rowStyle.borderBottom} !important`,
					},
				}),
			}}
			tabIndex={-1}
		>
			{columns.map((column, colIndex) => (
				<OptimizedTableCell
					key={`${index}-${colIndex}`}
					column={column}
					lrm={lrm}
					getCellStyle={getCellStyle}
					getCellContent={getCellContent}
				/>
			))}
		</TableRow>
	);
}

const OptimizedTableRow = memo(OptimizedTableRowComponent);

const prepareChangeObject = (groupData, startDate, description = 'Initial Rent') => {
	const { inputChangeAdvanceAmounts, maxAdvanceToMonth, inputChangeGrossRents, maxLeaseTerm } = processLeaseData(groupData);

	return {
		inputChangeMonth: 1,
		inputChangeGrossAmount: 0,
		inputChangeLeaseTerm: Number(maxLeaseTerm),
		inputChangeDiscountRate: String(groupData[0]['Incremental Borrowing Rate']),
		inputChangeEscalation: String(groupData[0]['Escalation (%)']),
		paymentFrequency:
			groupData[0]['Payment Frequency']?.toLowerCase() === 'monthly'
				? 'Monthly'
				: groupData[0]['Payment Frequency']?.toLowerCase() === 'quarterly'
					? 'Quarterly'
					: '',
		paymentTime:
			groupData[0]['Payment Modality']?.toLowerCase() === 'advance basis'
				? 'Start'
				: groupData[0]['Payment Modality']?.toLowerCase() === 'accrual basis'
					? 'End'
					: '',
		inputChangeAdvance: inputChangeAdvanceAmounts?.reduce((sum, item) => sum + Number(item), 0),
		inputChangeAmortizationPeriod: String(maxAdvanceToMonth),
		inputChangeEffectiveDate: utcConverter(dayjs(startDate)),
		inputChangeAdvanceAmounts,
		inputChangeGrossRents,
		inputChangeDescription: description,
		upfrontLeasePayment: String(groupData[0]['Upfront Lease Payment'] || 0),
		initialDirectCost: String(groupData[0]['Initial Direct Cost'] || 0),
		securityDeposit: String(groupData[0]['Security Deposit'] || 0),
		cashIncentiveAmount: String(groupData[0]['Cash Incentive'] || 0),
	};
};

const LeaseV9NewTrailModeBulkUpload = ({ vendorList }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { state } = useLocation();

	const [uiState, setUiState] = useState({
		show: false,
		showModal: false,
		fileSubmitButton: false,
		validationFailed: false,
	});

	const validateRentAdvanceMonths = (items) => {
		const errors = [];

		const rentToMonths = items.filter((item) => item['Rent (To Month)'] !== '-').map((item) => Number(item['Rent (To Month)']));

		const maxRentToMonth = rentToMonths.length > 0 ? Math.max(...rentToMonths) : 0;

		const advanceToMonths = items.filter((item) => item['Advance (To Month)'] !== '-').map((item) => Number(item['Advance (To Month)']));

		const maxAdvanceToMonth = advanceToMonths.length > 0 ? Math.max(...advanceToMonths) : 0;

		if (maxRentToMonth < maxAdvanceToMonth && maxAdvanceToMonth > 0) {
			errors.push({
				rowIndex: items[0]['Data No.'],
				originalRow: items[0],
				message: `Max Rent (To Month) ${maxRentToMonth} must be greater than or equal to max Advance (To Month) ${maxAdvanceToMonth}`,
			});
		}

		return errors;
	};

	const validateRentVsAdvance = (items) => {
		const errors = [];

		const totalRentAmount = items.filter((item) => item['Rent Amount'] !== '-').reduce((sum, item) => sum + Number(item['Rent Amount']), 0);

		const totalAdvanceAmount = items
			.filter((item) => item['Advance Adjustment Amount'] !== '-')
			.reduce((sum, item) => sum + Number(item['Advance Adjustment Amount']), 0);

		if (totalRentAmount < totalAdvanceAmount && totalAdvanceAmount > 0) {
			errors.push({
				rowIndex: items[0]['Data No.'],
				originalRow: items[0],
				message: `Total Rent Amount (${totalRentAmount}) must be greater than or equal to total Advance Adjustment Amount (${totalAdvanceAmount})`,
			});
		}

		return errors;
	};

	const setShowModal = useCallback((value) => {
		setUiState((prev) => ({ ...prev, showModal: value }));
	}, []);

	const setShow = useCallback((value) => {
		setUiState((prev) => ({ ...prev, show: value }));
	}, []);

	const setFileSubmitButton = useCallback((value) => {
		setUiState((prev) => ({ ...prev, fileSubmitButton: value }));
	}, []);

	const setValidationFailed = useCallback((value) => {
		setUiState((prev) => ({ ...prev, validationFailed: value }));
	}, []);

	const [data, setData] = useState({
		excelData: [],
		excelTable: [],
		modalInfo: null,
		validationErrors: [],
		emptyFileError: false,
	});

	const [uploadBulkIdentityCase, { error: errorData, isSuccess: isBulkCaseSuccess, isError: isBulkCaseError, isLoading: isBulkUploadLoading }] =
		useGetAllIdentityCaseMutation();
	const handleSubmitExcelData = useCallback(async () => {
		const groupedByReferenceId = _.groupBy(data.excelTable, 'Reference Id');
		const createPayloadArray = [];

		for (const referenceId in groupedByReferenceId) {
			const refItems = groupedByReferenceId[referenceId];

			const startDate = parseAndFormatExcelDate(refItems[0]['Lease Start Date (YYYY-MM-DD)']);
			const { inputChangeAdvanceAmounts, advanceAmountList, maxAdvanceToMonth, inputChangeGrossRents, grossRentList, maxLeaseTerm } =
				processLeaseData(refItems);

			const endDate = dayjs(startDate).add(+maxLeaseTerm, 'month').subtract(1, 'day').format('YYYY-MM-DD');

			const formattedStartDate = formatDate(startDate);
			const formattedEndDate = formatDate(endDate);
			const { day: startDay, month: startMonth, year: startYear } = getDateParts(dayjs(startDate).format('YYYY-MM-DD'));
			const changes = [prepareChangeObject(refItems, startDate, 'Initial Rent')];

			const validLessorName = vendorList?.find((lessor) => {
				const code = lessor?.code;
				const itemValue = refItems[0]['Lessor Code'];

				if (code == null || itemValue == null) return false;

				return Number(code) === Number(itemValue);
			});

			const leasePayload = {
				leaseTitle: refItems[0]['Lease Title'],
				leaseType: refItems[0]['Lease Type'],
				lessorName: validLessorName?.label,
				lessorID: validLessorName?.value,
				paymentFrequency: String(refItems[0]['Payment Frequency']),
				leaseStartDate: `${formattedStartDate}T00:00:00.000Z`,
				leaseEndDate: formattedEndDate,
				leaseTerm: Number(maxLeaseTerm),
				grossRent: 0,
				initialDiscountRate: String(refItems[0]['Incremental Borrowing Rate']),
				escalationPercentage: Number(refItems[0]['Escalation (%)']),
				paymentModality: String(
					refItems[0]['Payment Modality']?.toLowerCase() === 'advance basis'
						? 'Start'
						: refItems[0]['Payment Modality']?.toLowerCase() === 'accrual basis'
							? 'End'
							: ''
				),
				paymentModalityLabel: String(
					refItems[0]['Payment Modality']?.toLowerCase() === 'advance basis'
						? 'Advance Basis'
						: refItems[0]['Payment Modality']?.toLowerCase() === 'accrual basis'
							? 'Accrual Basis'
							: ''
				),
				advanceDuration: Number(maxAdvanceToMonth),
				advanceAmount: inputChangeAdvanceAmounts?.reduce((sum, item) => sum + Number(item), 0),
				changes,
				lastModifiedDate: dayjs(),
				endOfFinancialYear: dayjs().month(11).date(31),
				startDay,
				startMonth,
				startYear,
				advanceAmountList,
				grossRentList,
				advanceAmountListSet: [advanceAmountList],
				grossRentListSet: [grossRentList],
				grossRents: inputChangeGrossRents,
				advanceAmounts: inputChangeAdvanceAmounts,
				// upfrontLeasePayment: 0,
				// securityDeposit: 0,
				// initialDirectCost: 0,
				modificationSequence: 0,
				effectiveDateHistory: [`${formattedStartDate}T00:00:00.000Z`],
				referenceId: referenceId,
				upfrontLeasePayment: String(refItems[0]['Upfront Lease Payment'] || 0),
				initialDirectCost: String(refItems[0]['Initial Direct Cost'] || 0),
				securityDeposit: String(refItems[0]['Security Deposit'] || 0),
				cashIncentiveAmount: String(refItems[0]['Cash Incentive'] || 0),
				deletionMarker: true,
				modifyCount: 0,
				editCount: 0,
			};
			createPayloadArray.push(leasePayload);
		}

		const payload = {
			suffixCode: '',
			operation: 'add-draft-bulk',
			processCode: state?.processCode,
			data: createPayloadArray,
		};

		uploadBulkIdentityCase(payload);
	}, [data.excelTable, state?.processCode, uploadBulkIdentityCase, vendorList]);

	const setExcelTable = useCallback((value) => {
		setData((prev) => ({ ...prev, excelTable: value }));
	}, []);

	const handleExcelSubmit = useCallback(() => {
		setData((prev) => ({
			...prev,
			modalInfo: {
				head: 'Lease Bulk Upload',
				action: 'submit',
				activationStatus: 'process lease records',
			},
		}));
		setUiState((prev) => ({ ...prev, showModal: true, show: false }));
	}, []);

	useEffect(() => {
		const allValid = data.excelTable.every((item) => {
			return item['Remarks'].valid;
		});

		setFileSubmitButton(!allValid);
	}, [data.excelTable]);

	useEffect(() => {
		if (isBulkCaseSuccess) {
			toast.success('Lease records processed successfully!', toastConfig);
			navigate(-1);
		} else if (isBulkCaseError) {
			if (errorData?.data?.code === '501') {
				toast.error(errorData?.data?.message, toastConfig);
			} else {
				toast.error('Failed! Action not completed', toastConfig);
			}
		}
	}, [isBulkCaseSuccess, isBulkCaseError, errorData, navigate]);

	const getModifiedData = useCallback((data) => {
		const leaseIdToDataNo = {};
		let dataNoCounter = 1;

		const processedData = data.map((item) => {
			const leaseId = item?.['Reference Id'];

			if (!Object.hasOwn(leaseIdToDataNo, leaseId)) {
				leaseIdToDataNo[leaseId] = dataNoCounter;
				dataNoCounter++;
			}

			let remarks = {};

			if (item?.['Payment Frequency'] === '' || item?.['Payment Frequency'] === undefined) {
				remarks['Payment Frequency'] = 'Payment Frequency is required';
			} else if (item?.['Payment Frequency'] !== 'Monthly' && item?.['Payment Frequency'] !== 'Quarterly') {
				remarks['Payment Frequency'] = 'Payment Frequency must be either Monthly or Quarterly';
			}

			if (!item?.['Lease Start Date (YYYY-MM-DD)']) {
				remarks['Lease Start Date (YYYY-MM-DD)'] = 'Lease Start Date (YYYY-MM-DD) is required';
			}

			if (item?.['Rent Amount'] === '' || item?.['Rent Amount'] === undefined) {
				remarks['Rent Amount'] = 'Rent Amount is required. If not applicable, please enter -';
			} else if (isNaN(Number(item?.['Rent Amount'])) && item?.['Rent Amount'] !== '-') {
				remarks['Rent Amount'] = 'Rent Amount must be a number';
			}

			if (item?.['Rent (From Month)'] === '' || item?.['Rent (From Month)'] === undefined) {
				remarks['Rent (From Month)'] = 'Rent (From Month) is required. If not applicable, please enter -';
			} else if (isNaN(Number(item?.['Rent (From Month)'])) && item?.['Rent (From Month)'] !== '-') {
				remarks['Rent (From Month)'] = 'Rent (From Month) must be a number';
			}

			if (item?.['Rent (To Month)'] === '' || item?.['Rent (To Month)'] === undefined) {
				remarks['Rent (To Month)'] = 'Rent (To Month) is required. If not applicable, please enter -';
			} else if (isNaN(Number(item?.['Rent (To Month)'])) && item?.['Rent (To Month)'] !== '-') {
				remarks['Rent (To Month)'] = 'Rent (To Month) must be a number';
			}

			if (item?.['Advance (From Month)'] === '' || item?.['Advance (From Month)'] === undefined) {
				remarks['Advance (From Month)'] = 'Advance (From Month) is required. If not applicable, please enter -';
			} else if (isNaN(Number(item?.['Advance (From Month)'])) && item?.['Advance (From Month)'] !== '-') {
				remarks['Advance (From Month)'] = 'Advance (From Month) must be a number';
			}

			if (item?.['Advance (To Month)'] === '' || item?.['Advance (To Month)'] === undefined) {
				remarks['Advance (To Month)'] = 'Advance (To Month) is required. If not applicable, please enter -';
			} else if (isNaN(Number(item?.['Advance (To Month)'])) && item?.['Advance (To Month)'] !== '-') {
				remarks['Advance (To Month)'] = 'Advance (To Month) must be a number';
			}

			if (item?.['Advance Adjustment Amount'] === '' || item?.['Advance Adjustment Amount'] === undefined) {
				remarks['Advance Adjustment Amount'] = 'Advance Adjustment Amount is required. If not applicable, please enter -';
			} else if (isNaN(Number(item?.['Advance Adjustment Amount'])) && item?.['Advance Adjustment Amount'] !== '-') {
				remarks['Advance Adjustment Amount'] = 'Advance Adjustment Amount must be a number';
			}

			if (item?.['Incremental Borrowing Rate'] === '' || item?.['Incremental Borrowing Rate'] === undefined) {
				remarks['Incremental Borrowing Rate'] = 'Incremental Borrowing Rate is required';
			} else if (isNaN(Number(item?.['Incremental Borrowing Rate']))) {
				remarks['Incremental Borrowing Rate'] = 'Incremental Borrowing Rate must be a number';
			}

			if (item?.['Escalation (%)'] === '' || item?.['Escalation (%)'] === undefined) {
				remarks['Escalation (%)'] = 'Escalation (%) is required';
			} else if (isNaN(Number(item?.['Escalation (%)']))) {
				remarks['Escalation (%)'] = 'Escalation (%) must be a number';
			}

			if (item?.['Payment Modality'] === '' || item?.['Payment Modality'] === undefined) {
				remarks['Payment Modality'] = 'Payment Modality is required';
			} else if (
				item?.['Payment Modality']?.toLowerCase() !== 'advance basis' &&
				item?.['Payment Modality']?.toLowerCase() !== 'accrual basis'
			) {
				remarks['Payment Modality'] = 'Payment Modality must be either Advance Basis or Accrual Basis';
			}
			if (
				item?.['Upfront Lease Payment'] !== '' &&
				item?.['Upfront Lease Payment'] !== undefined &&
				isNaN(Number(item?.['Upfront Lease Payment']))
			) {
				remarks['Upfront Lease Payment'] = 'Upfront Lease Payment must be a number';
			}

			if (item?.['Initial Direct Cost'] !== '' && item?.['Initial Direct Cost'] !== undefined && isNaN(Number(item?.['Initial Direct Cost']))) {
				remarks['Initial Direct Cost'] = 'Initial Direct Cost must be a number';
			}

			if (item?.['Security Deposit'] !== '' && item?.['Security Deposit'] !== undefined && isNaN(Number(item?.['Security Deposit']))) {
				remarks['Security Deposit'] = 'Security Deposit must be a number';
			}

			if (item?.['Cash Incentive'] !== '' && item?.['Cash Incentive'] !== undefined && isNaN(Number(item?.['Cash Incentive']))) {
				remarks['Cash Incentive'] = 'Cash Incentive must be a number';
			}

			if (Object.keys(remarks).length === 0) {
				remarks = { valid: true };
			}

			return {
				'Data No.': leaseIdToDataNo[leaseId],
				...item,
				Remarks: remarks,
			};
		});

		return processedData;
	}, []);

	const validateData = useCallback(
		(data) => {
			if (!data || data.length === 0) {
				toast.error('Excel file is empty or contains no valid data. Please upload a file with data.', toastConfig);
				setShow(false);
				setExcelTable([]);
				setValidationFailed(true);
				setData((prev) => ({ ...prev, emptyFileError: true }));
				return;
			}

			if (data.length === 1 && Object.values(data[0]).every((value) => value === '')) {
				toast.error('Excel file contains headers but no data rows. Please add lease data to your file.', toastConfig);
				setShow(false);
				setExcelTable([]);
				setValidationFailed(true);
				setData((prev) => ({ ...prev, emptyFileError: true }));
				return;
			}

			const defaultKeys = Object.keys(INITIAL_EXPORT_DATA[0]);

			if (data.length > 0) {
				const firstRow = data[0];
				const missingColumns = defaultKeys.filter((key) => !(key in firstRow));

				if (missingColumns.length > 0) {
					toast.error(
						`Excel file is missing required columns: ${missingColumns.join(', ')}. Please use the template provided.`,
						toastConfig
					);
					setShow(false);
					setExcelTable([]);
					setValidationFailed(true);
					return;
				}
			}

			setValidationFailed(false);

			const normalizedData = data.map((obj) => {
				const newObj = { ...obj };
				defaultKeys.forEach((key) => {
					if (!Object.hasOwn(newObj, key)) {
						newObj[key] = '';
					}
				});
				return newObj;
			});

			let tableData = getModifiedData(normalizedData);

			const finalTableData = [];

			const groupedByReferenceId = _.groupBy(tableData, 'Reference Id');

			for (const referenceId in groupedByReferenceId) {
				const refItems = groupedByReferenceId[referenceId];

				refItems.forEach((item) => {
					const processedRemarks = {};
					let isValid = true;

					if (item['Payment Modality'] === '' || item['Payment Modality'] === undefined) {
						processedRemarks['Payment Modality Error'] = 'Payment Modality is required';
						isValid = false;
					} else if (
						item['Payment Modality']?.toLowerCase() !== 'advance basis' &&
						item['Payment Modality']?.toLowerCase() !== 'accrual basis'
					) {
						processedRemarks['Payment Modality Error'] = 'Payment Modality must be either Advance Basis or Accrual Basis';
						isValid = false;
					}

					if (item.Remarks && item.Remarks['Payment Frequency']) {
						processedRemarks['Payment Frequency'] = item.Remarks['Payment Frequency'];
						isValid = false;
					}

					if (item['Rent (From Month)'] !== '-' && item['Rent (To Month)'] !== '-') {
						const fromMonth = Number(item['Rent (From Month)']);
						const toMonth = Number(item['Rent (To Month)']);

						if (fromMonth > toMonth) {
							processedRemarks['RentFromToError'] =
								`Rent (From Month) ${fromMonth} must be less than or equal to Rent (To Month) ${toMonth}`;
							isValid = false;
						}
					}

					if (item['Advance (From Month)'] !== '-' && item['Advance (To Month)'] !== '-') {
						const fromMonth = Number(item['Advance (From Month)']);
						const toMonth = Number(item['Advance (To Month)']);

						if (fromMonth > toMonth) {
							processedRemarks['AdvanceFromToError'] =
								`Advance (From Month) ${fromMonth} must be less than or equal to Advance (To Month) ${toMonth}`;
							isValid = false;
						}
					}

					if (item['Payment Frequency'] === '' || item['Payment Frequency'] === undefined) {
						processedRemarks['Payment Frequency'] = 'Payment Frequency is required';
						isValid = false;
					} else if (item['Payment Frequency'] !== 'Monthly' && item['Payment Frequency'] !== 'Quarterly') {
						processedRemarks['Payment Frequency'] = 'Payment Frequency must be either Monthly or Quarterly';
						isValid = false;
					}

					if (
						item['Upfront Lease Payment'] !== '' &&
						item['Upfront Lease Payment'] !== undefined &&
						isNaN(Number(item['Upfront Lease Payment']))
					) {
						processedRemarks['Upfront Lease Payment'] = 'Upfront Lease Payment must be a number';
						isValid = false;
					}

					if (
						item['Initial Direct Cost'] !== '' &&
						item['Initial Direct Cost'] !== undefined &&
						isNaN(Number(item['Initial Direct Cost']))
					) {
						processedRemarks['Initial Direct Cost'] = 'Initial Direct Cost must be a number';
						isValid = false;
					}

					if (item['Security Deposit'] !== '' && item['Security Deposit'] !== undefined && isNaN(Number(item['Security Deposit']))) {
						processedRemarks['Security Deposit'] = 'Security Deposit must be a number';
						isValid = false;
					}

					if (item['Cash Incentive'] !== '' && item['Cash Incentive'] !== undefined && isNaN(Number(item['Cash Incentive']))) {
						processedRemarks['Cash Incentive'] = 'Cash Incentive must be a number';
						isValid = false;
					}

					processedRemarks.valid = isValid;

					const processedItem = {
						...item,
						Remarks: processedRemarks,
					};

					finalTableData.push(processedItem);
				});

				const rentItems = [...refItems]
					.filter((item) => item['Rent (From Month)'] !== '-' && item['Rent (To Month)'] !== '-')
					.sort((a, b) => Number(a['Rent (From Month)']) - Number(b['Rent (From Month)']));

				for (let i = 0; i < rentItems.length - 1; i++) {
					const current = rentItems[i];
					const next = rentItems[i + 1];

					const currentToMonth = Number(current['Rent (To Month)']);
					const nextFromMonth = Number(next['Rent (From Month)']);

					if (currentToMonth + 1 !== nextFromMonth) {
						const itemIndex = finalTableData.findIndex(
							(item) =>
								item['Reference Id'] === next['Reference Id'] &&
								item['Rent (From Month)'] === next['Rent (From Month)'] &&
								item['Rent (To Month)'] === next['Rent (To Month)']
						);

						if (itemIndex !== -1) {
							const errorMsg = `Rent sequence break: expected ${currentToMonth + 1} but got ${nextFromMonth}`;

							if (finalTableData[itemIndex].Remarks.valid) {
								finalTableData[itemIndex].Remarks = {
									'Rent Sequence Error': errorMsg,
									valid: false,
								};
							} else {
								finalTableData[itemIndex].Remarks['Rent Sequence Error'] = errorMsg;
								finalTableData[itemIndex].Remarks.valid = false;
							}
						}
					}
				}

				const advanceItems = [...refItems]
					.filter((item) => item['Advance (From Month)'] !== '-' && item['Advance (To Month)'] !== '-')
					.sort((a, b) => Number(a['Advance (From Month)']) - Number(b['Advance (From Month)']));

				for (let i = 0; i < advanceItems.length - 1; i++) {
					const current = advanceItems[i];
					const next = advanceItems[i + 1];

					const currentToMonth = Number(current['Advance (To Month)']);
					const nextFromMonth = Number(next['Advance (From Month)']);

					if (currentToMonth + 1 !== nextFromMonth) {
						const itemIndex = finalTableData.findIndex(
							(item) =>
								item['Reference Id'] === next['Reference Id'] &&
								item['Advance (From Month)'] === next['Advance (From Month)'] &&
								item['Advance (To Month)'] === next['Advance (To Month)']
						);

						if (itemIndex !== -1) {
							const errorMsg = `Advance sequence break: expected ${currentToMonth + 1} but got ${nextFromMonth}`;

							if (finalTableData[itemIndex].Remarks.valid) {
								finalTableData[itemIndex].Remarks = {
									'Advance Sequence Error': errorMsg,
									valid: false,
								};
							} else {
								finalTableData[itemIndex].Remarks['Advance Sequence Error'] = errorMsg;
								finalTableData[itemIndex].Remarks.valid = false;
							}
						}
					}
				}

				const uniqueLessorNames = [...new Set(refItems.map((item) => item['Lessor Code']))];

				if (uniqueLessorNames.length > 1) {
					const referenceLessorName = refItems[0]['Lessor Code'];

					refItems.forEach((item) => {
						if (item['Lessor Code'] !== referenceLessorName) {
							const itemIndex = finalTableData.findIndex(
								(tableItem) =>
									tableItem['Reference Id'] === item['Reference Id'] &&
									tableItem['Lessor Code'] === item['Lessor Code'] &&
									tableItem['Rent (From Month)'] === item['Rent (From Month)'] &&
									tableItem['Rent (To Month)'] === item['Rent (To Month)']
							);

							if (itemIndex !== -1) {
								const errorMsg = `Lessor Code must be consistent within the same reference ID group. Expected: ${referenceLessorName}`;

								if (finalTableData[itemIndex].Remarks.valid) {
									finalTableData[itemIndex].Remarks = {
										'Lessor Code Error': errorMsg,
										valid: false,
									};
								} else {
									finalTableData[itemIndex].Remarks['Lessor Code Error'] = errorMsg;
									finalTableData[itemIndex].Remarks.valid = false;
								}
							}
						}
					});
				}

				if (!isEmpty(uniqueLessorNames)) {
					refItems.forEach((item) => {
						const validLessorName = vendorList?.find((lessor) => {
							const code = lessor?.code;
							const itemValue = item?.['Lessor Code'];

							if (code == null || itemValue == null) return false;

							return Number(code) === Number(itemValue);
						});
						if (isEmpty(validLessorName)) {
							const itemIndex = finalTableData.findIndex(
								(tableItem) =>
									tableItem['Reference Id'] === item['Reference Id'] &&
									tableItem['Lessor Code'] === item['Lessor Code'] && // Match the specific lessor code
									tableItem['Rent (From Month)'] === item['Rent (From Month)'] &&
									tableItem['Rent (To Month)'] === item['Rent (To Month)']
							);

							if (itemIndex !== -1) {
								const errorMsg = `Lessor Code Not Found.`;

								if (finalTableData[itemIndex].Remarks.valid) {
									finalTableData[itemIndex].Remarks = {
										'Lessor Code Error': errorMsg,
										valid: false,
									};
								} else {
									finalTableData[itemIndex].Remarks['Lessor Code Error'] = errorMsg;
									finalTableData[itemIndex].Remarks.valid = false;
								}
							}
						}
					});
				}

				const consistencyErrors = validateConsistentFields(refItems);

				consistencyErrors.forEach((error) => {
					const itemIndex = finalTableData.findIndex(
						(item) =>
							item['Data No.'] === error.rowIndex &&
							item['Reference Id'] === error.referenceId &&
							item['Rent (From Month)'] === error.fromMonth &&
							item['Rent (To Month)'] === error.toMonth
					);

					if (itemIndex !== -1) {
						if (finalTableData[itemIndex].Remarks.valid) {
							finalTableData[itemIndex].Remarks = {
								'Consistency Error': error.message,
								valid: false,
							};
						} else {
							finalTableData[itemIndex].Remarks['Consistency Error'] = error.message;
							finalTableData[itemIndex].Remarks.valid = false;
						}
					}
				});

				const rentAdvanceMonthErrors = validateRentAdvanceMonths(refItems);
				rentAdvanceMonthErrors.forEach((error) => {
					const itemIndex = finalTableData.findIndex(
						(item) => item['Data No.'] === error.rowIndex && item['Reference Id'] === error.originalRow['Reference Id']
					);

					if (itemIndex !== -1) {
						const errorMsg = error.message;

						if (finalTableData[itemIndex].Remarks.valid) {
							finalTableData[itemIndex].Remarks = {
								'Rent Advance Month Error': errorMsg,
								valid: false,
							};
						} else {
							finalTableData[itemIndex].Remarks['Rent Advance Month Error'] = errorMsg;
							finalTableData[itemIndex].Remarks.valid = false;
						}
					}
				});

				const rentVsAdvanceErrors = validateRentVsAdvance(refItems);
				rentVsAdvanceErrors.forEach((error) => {
					const itemIndex = finalTableData.findIndex(
						(item) => item['Data No.'] === error.rowIndex && item['Reference Id'] === error.originalRow['Reference Id']
					);

					if (itemIndex !== -1) {
						const errorMsg = error.message;

						if (finalTableData[itemIndex].Remarks.valid) {
							finalTableData[itemIndex].Remarks = {
								'Rent vs Advance Error': errorMsg,
								valid: false,
							};
						} else {
							finalTableData[itemIndex].Remarks['Rent vs Advance Error'] = errorMsg;
							finalTableData[itemIndex].Remarks.valid = false;
						}
					}
				});
			}

			finalTableData.sort((a, b) => a['Data No.'] - b['Data No.']);

			const groupedByRefId = _.groupBy(finalTableData, 'Reference Id');

			const groupedFinalData = Object.values(groupedByRefId)
				.flatMap((refItems, index) => {
					const groupId = index + 1;
					return refItems.map((item) => ({
						...item,
						groupId: groupId,
					}));
				})
				.sort((a, b) => a['Data No.'] - b['Data No.']);

			setData((prev) => ({ ...prev, excelData: normalizedData }));
			setExcelTable(groupedFinalData);
		},
		[getModifiedData, setExcelTable, vendorList]
	);

	const getCellStyle = useCallback((columnKey, value, lrm) => {
		switch (columnKey) {
			case 'Lessor Code':
				return {
					border: lrm.Remarks && lrm.Remarks['Lessor Code Error'] ? '2px solid red' : '',
				};

			case 'Incremental Borrowing Rate':
				return {
					border:
						value === '' ||
						isNaN(Number(value)) ||
						(lrm.Remarks && lrm.Remarks['Consistency Error'] && lrm.Remarks['Consistency Error'].includes('Incremental Borrowing Rate'))
							? '2px solid red'
							: '',
				};

			case 'Payment Frequency':
				return {
					border: value === '' || !(value?.toLowerCase() === 'monthly' || value?.toLowerCase() === 'quarterly') ? '2px solid red' : '',
				};

			case 'Lease Start Date (YYYY-MM-DD)':
				return {
					border: value === '' ? '2px solid red' : '',
				};

			case 'Rent (From Month)':
				return {
					border: value !== '-' && (value === '' || isNaN(Number(value))) ? '2px solid red' : '',
				};

			case 'Rent (To Month)':
				return {
					border: value !== '-' && (value === '' || isNaN(Number(value))) ? '2px solid red' : '',
				};

			case 'Rent Amount':
				return {
					border: value !== '-' && (value === '' || isNaN(Number(value))) ? '2px solid red' : '',
				};

			case 'Advance (From Month)':
				return {
					border: value !== '-' && (value === '' || isNaN(Number(value))) ? '2px solid red' : '',
				};

			case 'Advance (To Month)':
				return {
					border: lrm['Advance (From Month)'] !== '-' && (value === '' || isNaN(Number(value))) ? '2px solid red' : '',
				};

			case 'Advance Adjustment Amount':
				return {
					border: value !== '-' && (value === '' || isNaN(Number(value))) ? '2px solid red' : '',
				};

			case 'Escalation (%)':
				return {
					border: value === '' || isNaN(Number(value)) ? '2px solid red' : '',
				};

			case 'Payment Modality':
				return {
					border:
						value === '' || !(value?.toLowerCase() === 'advance basis' || value?.toLowerCase() === 'accrual basis')
							? '2px solid red'
							: '',
				};
			case 'Upfront Lease Payment':
				return {
					border: value !== '' && value !== undefined && isNaN(Number(value)) ? '2px solid red' : '',
				};
			case 'Initial Direct Cost':
				return {
					border: value !== '' && value !== undefined && isNaN(Number(value)) ? '2px solid red' : '',
				};
			case 'Security Deposit':
				return {
					border: value !== '' && value !== undefined && isNaN(Number(value)) ? '2px solid red' : '',
				};
			case 'Cash Incentive':
				return {
					border: value !== '' && value !== undefined && isNaN(Number(value)) ? '2px solid red' : '',
				};
			default:
				return {};
		}
	}, []);

	const getCellContent = useCallback(
		(columnKey, value, lrm) => {
			switch (columnKey) {
				case 'Reference Id':
					return value;

				case 'Lease Title':
					return value;

				case 'Lease Type':
					return value;

				case 'Lessor Code':
					return value;

				case 'Payment Frequency':
					if (value === '') return ' Required ';
					if (value?.toLowerCase() === 'monthly' || value?.toLowerCase() === 'quarterly') {
						return value;
					}
					return ' Enter monthly or quarterly as value ';

				case 'Lease Start Date (YYYY-MM-DD)':
					return value === '' ? ' Required ' : formatDate(parseAndFormatExcelDate(value));

				case 'Rent (From Month)':
					if (value === '') return ' Required ';
					if (value === '-') return '-';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return value;

				case 'Rent (To Month)':
					if (value === '') return ' Required ';
					if (lrm['Rent (From Month)'] === '-') return '-';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return value;

				case 'Rent Amount':
					if (value === '') return ' Required ';
					if (value === '-') return '-';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return formatNumber(value, 2);

				case 'Advance (From Month)':
					if (value === '') return ' Required ';
					if (value === '-') return '-';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return value;

				case 'Advance (To Month)':
					if (value === '') return ' Required ';
					if (lrm['Advance (From Month)'] === '-') return '-';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return value;

				case 'Advance Adjustment Amount':
					if (value === '') return ' Required ';
					if (value === '-') return '-';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return formatNumber(value);

				case 'Incremental Borrowing Rate':
					if (value === '') return ' Required ';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return value;

				case 'Escalation (%)':
					if (value === '') return ' Required ';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return value;

				case 'Payment Modality':
					if (value === '') return ' Required ';
					if (value?.toLowerCase() === 'advance basis' || value?.toLowerCase() === 'accrual basis') {
						return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
					}
					return ' Enter Advance Basis or Accrual Basis as value ';
				case 'Upfront Lease Payment':
					if (value === '' || value === undefined) return '0';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return formatNumber(value);
				case 'Initial Direct Cost':
					if (value === '' || value === undefined) return '0';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return formatNumber(value);
				case 'Security Deposit':
					if (value === '' || value === undefined) return '0';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return formatNumber(value);
				case 'Cash Incentive':
					if (value === '' || value === undefined) return '0';
					if (isNaN(Number(value))) return ' Insert a Numeric Value ';
					return formatNumber(value);
				case 'Action':
					return (
						<div>
							{lrm['Remarks'].valid ? (
								<CheckSquareOutlined
									size="large"
									style={{
										fontSize: '20px',
										color: theme.palette.lynTheme.success,
									}}
								/>
							) : (
								<Popover
									trigger="click"
									placement="left"
									title={
										<div style={{ fontWeight: 'bold', textAlign: 'center' }}>
											Validation Errors - Lease Reference ID: {lrm['Reference Id']}
										</div>
									}
									content={<ErrorModal modalData={transformErrors(lrm['Remarks'], lrm['Data No.'])} />}
									overlayStyle={{ maxWidth: '500px' }}
								>
									<Button
										style={{
											backgroundColor: theme.palette.error?.main || 'red',
											marginRight: '10px',
										}}
										type="primary"
									>
										Show Errors ({Object.keys(lrm['Remarks']).filter((k) => k !== 'valid').length})
									</Button>
								</Popover>
							)}
						</div>
					);

				default:
					return value;
			}
		},
		[theme.palette.lynTheme.success, theme.palette.error?.main]
	);

	return (
		<div>
			{!uiState.validationFailed && (
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						paddingLeft: '20px',
						paddingRight: '20px',
						marginBottom: '10px',
					}}
				>
					<p style={{ margin: 0 }}>
						To download a demo <img src={excel} alt="excel" width={22} style={{ margin: '0 5px' }} /> file{' '}
						<Button
							size="small"
							type="dashed"
							onClick={(e) => {
								e.stopPropagation();
								exportExcelFileWithGuidelines(INITIAL_EXPORT_DATA, 'LRM_BulkUpload.xlsx');
							}}
							style={{ color: '#1677ff', borderColor: '#1677ff' }}
						>
							Click Here
						</Button>
					</p>

					{data.excelTable && data.excelTable.length > 0 && (
						<div
							style={{
								display: 'flex',
								gap: '10px',
								fontSize: '16px',
								fontWeight: '500',
								flexDirection: 'column',
							}}
						>
							<span style={{ color: '#1677ff', display: 'flex' }}>
								<span style={{ width: '140px' }}>No. of Lease Count</span>
								<span>: {new Set(data.excelTable.map((item) => item['Reference Id'])).size}</span>
							</span>
							<span style={{ color: '#52c41a', display: 'flex' }}>
								<span style={{ width: '140px' }}>Total Row Count</span>
								<span>: {data.excelTable.length}</span>
							</span>
						</div>
					)}
				</div>
			)}

			<Spin spinning={isBulkUploadLoading}>
				<Row gutter={[24, 24]}>
					<Col xs={24} sm={24} md={24} xl={24}>
						<Form.Item
							rules={[
								{
									required: true,
								},
							]}
						>
							{(!uiState?.show || uiState.validationFailed) && (
								<>
									<Dragger
										name="file"
										customRequest={({ file }) => {
											handleFileUpload(file, validateData, setShow);
										}}
										showUploadList={false}
										accept=".xlsx, .xls"
									>
										<p className="ant-upload-drag-icon">
											<InboxOutlined />
										</p>
										<p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
										<p className="ant-upload-hint">Support for .xlsx and .xls files.</p>
									</Dragger>

									{uiState.validationFailed && (
										<div
											style={{
												marginTop: '20px',
												textAlign: 'center',
												padding: '16px',
												background: '#f6f6f6',
												border: '1px dashed #d9d9d9',
												borderRadius: '4px',
											}}
										>
											<p style={{ color: '#ff4d4f', marginBottom: '8px', fontWeight: 'bold' }}>
												The uploaded Excel file is empty. Please upload a file that contains lease data.
											</p>
											<Button
												size="small"
												type="primary"
												onClick={(e) => {
													e.stopPropagation();
													exportExcelFile(INITIAL_EXPORT_DATA, 'LRM_BulkUpload.xlsx');
												}}
												style={{ backgroundColor: '#1e3a8a' }}
											>
												Download Template
											</Button>
										</div>
									)}
								</>
							)}

							<br></br>

							{uiState.show && data.excelData && !uiState.validationFailed && (
								<Box>
									<TableContainer
										sx={{
											width: '100%',
											overflowX: 'auto',
											overflowY: 'auto',
											position: 'relative',
											display: 'block',
											maxWidth: '100%',
											maxHeight: '70vh',
											height: '70vh',
											'& td, & th': { whiteSpace: 'nowrap' },
											border: '1px solid #000000',
											borderRadius: '4px',
											padding: '0px',
											boxSizing: 'border-box',
										}}
									>
										<Table
											stickyHeader
											aria-labelledby="tableTitle"
											sx={{
												'& .MuiTableCell-root:first-of-type': {
													pl: 2,
												},
												'& .MuiTableCell-root:last-of-type': {
													pr: 3,
												},
												borderCollapse: 'collapse',
											}}
										>
											<TableHead>
												<TableRow
													style={{
														backgroundColor: '#e5e5e5',
													}}
												>
													{TABLE_COLUMNS.map((headCell, index) => (
														<TableCell
															key={index}
															align={headCell.align || 'left'}
															padding={headCell.disablePadding ? 'none' : 'normal'}
															style={{
																zIndex: 1,
															}}
														>
															{headCell.label === 'Row No' && (
																<img src={excel} alt="excel" width={22} style={{ marginRight: '5px' }} />
															)}{' '}
															{headCell.label}
														</TableCell>
													))}
												</TableRow>
											</TableHead>

											<TableBody>
												{data.excelTable?.map((lrm, index) => (
													<OptimizedTableRow
														key={`${lrm['Data No.']}-${index}`}
														lrm={lrm}
														index={index}
														columns={TABLE_COLUMNS}
														getCellStyle={getCellStyle}
														getCellContent={getCellContent}
														tableData={data.excelTable}
														getRowStyle={getRowStyle}
													/>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							)}
						</Form.Item>
					</Col>

					<Col xs={24} sm={24} md={24} xl={24}>
						<Flex justify="flex-end" align="flex-end">
							{uiState.show && !uiState.validationFailed && (
								<>
									<Button
										style={{ backgroundColor: theme.palette.error?.main, marginRight: '10px' }}
										type="primary"
										onClick={(e) => {
											e.preventDefault();
											setExcelTable([]);
											setShow(false);
											setValidationFailed(false);
											setData((prev) => ({ ...prev, emptyFileError: false }));
										}}
									>
										Cancel
									</Button>

									<Button
										disabled={uiState?.fileSubmitButton}
										style={{ backgroundColor: uiState?.fileSubmitButton ? '' : theme.palette.lynTheme.submit }}
										type="primary"
										onClick={(e) => {
											e.preventDefault();
											handleExcelSubmit();
										}}
									>
										Submit
									</Button>
								</>
							)}
						</Flex>
					</Col>
				</Row>
			</Spin>
			{/*</Form>*/}

			{uiState?.showModal && (
				<ComptrollerConfirmModal
					showModal={uiState?.showModal}
					setShowModal={setShowModal}
					modalData={data?.modalInfo}
					handleSubmit={handleSubmitExcelData}
				/>
			)}
		</div>
	);
};

export default LeaseV9NewTrailModeBulkUpload;
